import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { BodyLarge, Button, H3 } from '@atlassian/mpac-primitives';
import { useHistory } from '@atlassian/mpac-ui';
import { css } from '@compiled/react';
import React from 'react';

import type { BundleConfig } from '../config/bundleConfig';

const footerWrapperStyles = css({
  position: 'relative',
  marginBottom: '8.125rem',
  paddingTop: token('space.400'),
  paddingBottom: token('space.400'),
});

// Blue background layer - rotated
const blueBackgroundStyles = css({
  position: 'absolute',
  top: '50%',
  left: '-3.2%',
  right: '0',
  height: '100%',
  transform: 'translateY(-50%) rotate(-3.5deg)',
  backgroundColor: '#1868DB',
  zIndex: -2,
});

// Grey background layer - rotated slightly less
const greyBackgroundStyles = css({
  position: 'absolute',
  top: '50%',
  left: '-2%',
  right: '-2%',
  height: '100%',
  transform: 'translateY(-50%) rotate(1deg)',
  backgroundColor: '#F0F1F2',
  zIndex: -1,
});

const footerContentContainerStyles = css({
  position: 'relative',
  padding: `${token('space.600')} ${token('space.300')}`,
  zIndex: 1,
  [media.below.md]: {
    padding: `${token('space.400')} ${token('space.300')}`,
  },
  [media.below.xs]: {
    padding: `${token('space.200')} ${token('space.400')}`,
  },
});

const footerContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [media.above.md]: {
    justifyContent: 'space-between',
  },
});

const footerContentStyles = css({
  maxWidth: '30rem',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: `0 ${token('space.200')}`,
});

const footerButtonStyles = css({
  marginTop: token('space.300'),
});

const footerImageStyles = css({
  display: 'none',
  [media.above.md]: {
    display: 'block',
  },
});

const h3Styles = css({
  fontWeight: '700',
});

interface FooterSectionProps {
  onRequestClick: () => void;
  bundleConfig: BundleConfig;
}

export const FooterSection: React.FC<FooterSectionProps> = ({ onRequestClick, bundleConfig }) => {
  const history = useHistory();

  const handleFooterCTAClick = () => {
    if (bundleConfig.footer.footerCTA) {
      if (bundleConfig.footer.footerCTA.type === 'internal') {
        // Use history.push for internal navigation
        history?.push(bundleConfig.footer.footerCTA.url);
      } else {
        // For external links, use window.location.href
        window.location.href = bundleConfig.footer.footerCTA.url;
      }
    }
  };

  return (
    <div css={footerWrapperStyles}>
      <div css={blueBackgroundStyles} />
      <div css={greyBackgroundStyles} />
      <div css={footerContentContainerStyles}>
        <div css={footerContainerStyles}>
          <div css={footerImageStyles}>
            <img
              src={bundleConfig.footer.leftImage}
              alt={bundleConfig.footer.leftImageAlt}
              width="240px"
            />
          </div>
          <div css={footerContentStyles}>
            <H3 css={h3Styles}>{bundleConfig.footer.title}</H3>
            <BodyLarge>{bundleConfig.footer.description}</BodyLarge>
            {bundleConfig.footer.requestButton && (
              <div css={footerButtonStyles}>
                <Button appearance="primary" onClick={onRequestClick}>
                  {bundleConfig.footer.requestButton}
                </Button>
              </div>
            )}
            {bundleConfig.footer.footerCTA && (
              <div css={footerButtonStyles}>
                <Button appearance="primary" onClick={handleFooterCTAClick}>
                  {bundleConfig.footer.footerCTA.text}
                </Button>
              </div>
            )}
          </div>
          <div css={footerImageStyles}>
            <img
              src={bundleConfig.footer.rightImage}
              alt={bundleConfig.footer.rightImageAlt}
              width="270px"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
